import { joiResolver } from '@hookform/resolvers/joi';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { MutationStatus } from 'react-query';
import { AxiosError } from 'axios';
import { usePostNonNationalExamSchoolingDetail } from '../../../api-refactored/non-national-exam-registration/usePostNonNationalExamSchoolingDetail';
import { ExamSchoolFormDataSPERS, SchoolFormData } from '../../../models';
import examSchoolingSchema from '../schema/examSchoolingSPERS';
import RouteKey from '../../../routes/key';
import { GetNonNationalExamSchoolingDetailsResponse } from '../../../api-refactored/non-national-exam-registration/useGetNonNationalExamSchoolingDetails';
import { ExamSchoolingDetailsFormSPERSLocation } from '../components/nonNationalExam/spers/ExamSchoolingDetailsForm';
import { EXAM_TYPE, NonNationalExamNavigationContext } from '../../../routes/ExamRoute';
import { GenericErrorResponse } from '../../../api-refactored/typings';
import { useGetNonNationalCandidateDetails } from '../../../api-refactored/non-national-exam-registration/useGetNonNationalCandidateDetails';
import { useContext, useState } from 'react';
import { useGetPassportDisabled } from '../../../api-refactored/non-national-exam-registration/useGetPassportDisabled';
import { NonNexPassportDisableContext } from '../../../routes/RootRouter';

const { uploadSupportingDocuments, confirmParticulars } = RouteKey.exam.child;

interface ExamSchoolingDetailsProps {
  uuid: string;
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse | undefined;
  isOpsAdmin?: boolean;
}

interface ExamSchoolingDetailsReturn {
  /**
   * Perform submit action, either to call block call or unblock call, depends on the account status
   */
  submitForm: (submitData: ExamSchoolFormDataSPERS, schools: SchoolFormData[]) => void;
  /**
   * Form to send edit & delete
   */
  formHook: UseFormReturn<ExamSchoolFormDataSPERS>;
  /**
   * React query mutation object
   */
  postExamSchoolingDetailMutationStatus: MutationStatus;
  isAeis: boolean;
  postError: AxiosError<GenericErrorResponse> | null;
  isErrorPost: boolean;
  nextPage: () => void;
}

/**
 * A hook to handle search functionality
 * @returns
 */
export const useExamSchoolingDetailsSPERS = ({
  uuid,
  examSchoolingDetailsData,
  isOpsAdmin = false,
}: ExamSchoolingDetailsProps): ExamSchoolingDetailsReturn => {
  const { state } = useLocation<ExamSchoolingDetailsFormSPERSLocation>();
  const history = useHistory();
  const isAeis = state?.examType === EXAM_TYPE.AEIS || state?.examType === EXAM_TYPE.SAEIS;

  const formHook = useForm<ExamSchoolFormDataSPERS>({
    mode: 'onSubmit',
    resolver: joiResolver(examSchoolingSchema(isOpsAdmin)),
    defaultValues:
      examSchoolingDetailsData &&
      Object.prototype.hasOwnProperty.call(examSchoolingDetailsData, 'schoolInfo')
        ? {
            hasStudyingInSingapore: examSchoolingDetailsData.schoolInfo ? 'yes' : 'no',
            selectedAdmissionLevel: examSchoolingDetailsData.selectedAdmissionLevel,
          }
        : {
            hasStudyingInSingapore: !isAeis ? 'yes' : undefined,
          },
  });

  const nonNexPassportDisableContext = useContext(NonNexPassportDisableContext);

  const {
    mutate: postExamSchoolingDetailMutation,
    status: postExamSchoolingDetailMutationStatus,
    isError,
    error,
  } = usePostNonNationalExamSchoolingDetail({
    uuid,
    onSuccess: () => nextPage(),
  });

  const nextPage = () => {
    const pathname = nonNexPassportDisableContext.isPassportDisabled
      ? `${confirmParticulars.route}`
      : `${uploadSupportingDocuments.route}`;
    history.push({
      pathname,
      state: {
        examType: state?.examType,
        examTitle: state?.examTitle,
        levelSeekingAdmission: state?.levelSeekingAdmission,
        isSupplementary: state?.isSupplementary,
      },
    });
  };

  const { data: candidateDetailsData } = useGetNonNationalCandidateDetails({
    uuid,
    onSuccess: () => null,
    onError: () => null,
  });

  const submitForm = (submitData: ExamSchoolFormDataSPERS, schools: SchoolFormData[]) => {
    if (examSchoolingDetailsData?.lockedByUser) {
      nextPage();
      return;
    }

    postExamSchoolingDetailMutation({
      caeCertificateDetailsModel: null,
      schoolInfo: submitData.hasStudyingInSingapore === 'yes',
      siblingInfo: false,
      examSchoolInfoModels:
        submitData.hasStudyingInSingapore === 'yes'
          ? schools.map((value) => ({
              country:
                typeof value.country !== 'string'
                  ? {
                      code: value.country.code,
                      description: value.country.description,
                    }
                  : { code: '', description: '' },
              dateOfAdmission: dayjs(value.dateOfAdmission).format('YYYY-MM-DDTHH:mm:ss'),
              dateOfCompletion: dayjs(value.dateOfCompletion).format('YYYY-MM-DDTHH:mm:ss'),
              highestLevelPassed: value.highestLevelPassed,
              schoolName: value.schoolName,
              typeOfSchool: value.typeOfSchool,
            }))
          : [],
      examSiblingInfoModels: [],
      selectedAdmissionLevel: submitData.selectedAdmissionLevel,
      motherTongueLanguageToBeTaken: candidateDetailsData?.motherTongueLanguageToBeTaken,
    });
  };

  return {
    formHook,
    submitForm,
    postExamSchoolingDetailMutationStatus,
    isAeis,
    isErrorPost: isError,
    postError: error,
    nextPage,
  };
};
